"use client";

import Link from "next/link";
import { getClass as getButtonClass } from "@/lib/components/Button";
import React from "react";
import { Prisma } from "@prisma/client";
import { toUKDateString } from "@/lib/utils";
import Status from "@/lib/components/Status";
import Tag from "./Tag";

export type Project = Prisma.ProjectGetPayload<{
  include: { sessions: true; tags: { include: { tag: true } } };
}>;

export type Action = { label: string } & (
  | { href: string }
  | { onClick: () => void }
);

export default function ProjectListing(props: {
  project: Project;
  actions: Action[];
  programmeName?: string;
  organisationName?: string;
  highlight?: boolean;
}): React.ReactNode {
  const numberOfSessions = props.project.sessions.length;

  const [firstSession] = props.project.sessions;
  const lastSession = firstSession ? toUKDateString(firstSession.date) : null;

  return (
    <div
      className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 ring ring-gray rounded-lg p-2 sm:p-3 bg-linear-to-br from-white to-green-50 w-full ${props.highlight ? "ring-2 to-green-200" : "ring to-green-50"}`}
    >
      <div className="flex flex-col gap-3 flex-1">
        <div className="flex-1 flex flex-row flex-wrap gap-4 items-center justify-start">
          <h2 className="text-lg font-bold">{props.project.name}</h2>
          <Status object={props.project} />
        </div>
        {props.organisationName && (
          <p>
            Organisation: <strong>{props.organisationName}</strong>
          </p>
        )}
        {props.programmeName && (
          <p>
            Programme: <strong>{props.programmeName}</strong>
          </p>
        )}
        {lastSession && (
          <p>
            <strong>{numberOfSessions}</strong> sessions (latest:{" "}
            <strong>{lastSession}</strong>)
          </p>
        )}
        {props.project.tags.length > 0 && (
          <div className="flex flex-row gap-2 flex-wrap">
            {props.project.tags.map(({ tag }) => (
              <Tag key={tag.id} value={tag.value} />
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 self-end sm:self-center items-end">
        {props.actions.map((action, i) =>
          "href" in action ? (
            <Link
              key={i}
              href={action.href}
              className={`${getButtonClass({ size: "sm", variant: "light" })} text-center`}
            >
              {action.label}
            </Link>
          ) : (
            <button
              key={i}
              onClick={action.onClick}
              className={`${getButtonClass({ size: "sm", variant: "light" })} text-center`}
            >
              {action.label}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
